/**
 * Fresh Start Furniture — scroll reveal.
 *
 * Adds `is-visible` to any [data-fsf-reveal] element once it scrolls into
 * view, so sections can fade and lift their content in. Progressive
 * enhancement: without JS, or with reduced motion preferred, everything is
 * simply visible from the start (see the `no-js` / reduced-motion rules in
 * assets/fsf.css).
 *
 * Elements are unobserved after firing — this is a one-shot reveal, not a
 * scroll-linked effect that re-runs every time you scroll past.
 */

const SELECTOR = '[data-fsf-reveal]';

function revealAll(root = document) {
  root.querySelectorAll(SELECTOR).forEach((el) => el.classList.add('is-visible'));
}

function init() {
  // Respect the user's motion preference, and bail out gracefully on very old
  // browsers by just showing everything.
  const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  if (prefersReduced || !('IntersectionObserver' in window)) {
    revealAll();
    return;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      });
    },
    {
      // Fire a little before the element is fully on screen so the motion
      // finishes about when the reader gets there.
      rootMargin: '0px 0px -12% 0px',
      threshold: 0.1,
    }
  );

  document.querySelectorAll(SELECTOR).forEach((el) => observer.observe(el));
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}

// The theme editor re-renders sections without a full page load, so re-scan
// and show anything new immediately rather than leaving it invisible.
document.addEventListener('shopify:section:load', (event) => {
  revealAll(event.target);
});
